// portfolioValuation.ts
// Fonctions utilitaires pour valorisation, allocation, stress tests, risk analysis

import { PortfolioPosition } from '../portfolio/model/PortfolioPosition';
import { ManualPortfolioPosition } from './portfolioStore';

export function computePortfolioValuation(
  exchangesPositions: PortfolioPosition[],
  manualPositions: ManualPortfolioPosition[],
  cryptoTable: any[],
  onchainAssets: any[]
) {
  const runtimePriceBySymbol = new Map<string, number>();
  if (Array.isArray(cryptoTable)) {
    cryptoTable.forEach((entry: any) => {
      const symbol = typeof entry?.symbol === 'string' ? entry.symbol.toUpperCase() : undefined;
      const price = typeof entry?.priceUsd === 'number'
        ? entry.priceUsd
        : (typeof entry?.currentPrice === 'number' ? entry.currentPrice : undefined);
      if (symbol && typeof price === 'number' && !isNaN(price)) {
        const existingPrice = runtimePriceBySymbol.get(symbol);
        if (existingPrice === undefined || existingPrice <= 0 || price > 0) {
          runtimePriceBySymbol.set(symbol, price);
        }
      }
    });
  }

  const isPositiveNumber = (value: unknown): value is number => typeof value === 'number' && !isNaN(value) && value > 0;

  // Harmonise assetId en majuscules pour tous les assets
  const all = [...exchangesPositions, ...manualPositions, ...onchainAssets].map(pos => {
    const normalizedAssetId = typeof pos.assetId === 'string' ? pos.assetId.toUpperCase() : pos.assetId;
    const runtimePrice = typeof normalizedAssetId === 'string' ? runtimePriceBySymbol.get(normalizedAssetId) : undefined;
    const storedPrice = isPositiveNumber(pos.price) ? pos.price : undefined;
    const sourceType = String((pos as any).sourceType ?? (pos as any).type ?? '').toUpperCase();
    const usedPrice = sourceType === 'MANUAL'
      ? (storedPrice ?? runtimePrice)
      : (runtimePrice ?? storedPrice);
    const storedValue = isPositiveNumber(pos.value) ? pos.value : undefined;
    let usedValue = (typeof usedPrice === 'number' && typeof pos.quantity === 'number' && !isNaN(pos.quantity))
      ? usedPrice * pos.quantity
      : (storedValue ?? 0);
    if (typeof usedValue !== 'number' || isNaN(usedValue)) usedValue = 0;
    return {
      ...pos,
      assetId: normalizedAssetId,
      price: usedPrice,
      value: usedValue
    };
  });
  const aggMap: { [assetId: string]: { value: number } } = {};
  all.forEach((pos) => {
    if (!aggMap[pos.assetId]) aggMap[pos.assetId] = { value: 0 };
    const v = typeof pos.value === 'number' && !isNaN(pos.value) ? pos.value : 0;
    aggMap[pos.assetId].value += v;
  });
  const total = all.reduce((sum, pos) => sum + (typeof pos.value === 'number' && !isNaN(pos.value) ? pos.value : 0), 0);
  const allocationArr = Object.keys(aggMap).map(assetId => ({
    assetId,
    percent: total > 0 ? (aggMap[assetId].value / total) * 100 : 0
  }));
  return { all, total, allocationArr };
}

export function computeStressTests(allocationArr: { assetId: string, percent: number }[]) {
  const stressTests: { label: string; color: string; icon: string }[] = [];
  const btc = allocationArr.find(a => a.assetId === 'BTC');
  if (btc && btc.percent > 60) {
    stressTests.push({ label: 'Concentration sur BTC > 60%', color: '#ff9800', icon: '⚠' });
  }
  const stablecoins = ['USDT', 'USDC', 'BSC-USD', 'DAI', 'TUSD', 'BUSD', 'EURT', 'PAX', 'GUSD'];
  const stablePct = allocationArr.filter(a => stablecoins.includes(a.assetId)).reduce((sum, a) => sum + a.percent, 0);
  if (stablePct < 5) {
    stressTests.push({ label: 'Absence de stablecoins (<5%)', color: '#e53935', icon: '❗' });
  }
  const sorted = [...allocationArr].sort((a, b) => b.percent - a.percent);
  const top2 = sorted.slice(0, 2).reduce((sum, a) => sum + a.percent, 0);
  const top3 = sorted.slice(0, 3).reduce((sum, a) => sum + a.percent, 0);
  if (top2 > 80 || top3 > 80) {
    stressTests.push({ label: 'Top 2 ou 3 actifs > 80% du portefeuille', color: '#ff9800', icon: '⚠' });
  }
  if (allocationArr.length === 0) {
    stressTests.push({ label: 'Aucun actif dans le portefeuille', color: '#e53935', icon: '❗' });
  }
  return stressTests;
}

export function computeRiskAnalysis(allocationArr: { assetId: string, percent: number }[], all: any[]) {
  const riskAnalysis: string[] = [];
  const btcAlloc = allocationArr.find(a => a.assetId === 'BTC');
  if (btcAlloc && btcAlloc.percent > 60) riskAnalysis.push('Surconcentration sur BTC (>60%)');
  const ethAlloc = allocationArr.find(a => a.assetId === 'ETH');
  if (ethAlloc && ethAlloc.percent > 40) riskAnalysis.push('Surconcentration sur ETH (>40%)');
  const stablecoins = ['USDT', 'USDC', 'BSC-USD', 'DAI', 'TUSD', 'BUSD', 'EURT', 'PAX', 'GUSD'];
  const stablePctRisk = allocationArr.filter(a => stablecoins.includes(a.assetId)).reduce((sum, a) => sum + a.percent, 0);
  if (stablePctRisk < 5) riskAnalysis.push('Absence ou part très faible de stablecoins (<5%)');
  const sorted = [...allocationArr].sort((a, b) => b.percent - a.percent);
  const top2Risk = sorted.slice(0, 2).reduce((sum, a) => sum + a.percent, 0);
  const top3Risk = sorted.slice(0, 3).reduce((sum, a) => sum + a.percent, 0);
  if (top2Risk > 80) riskAnalysis.push('Top 2 actifs > 80% du portefeuille');
  if (top3Risk > 90) riskAnalysis.push('Top 3 actifs > 90% du portefeuille');
  const illiquidTokens = all.filter(a => typeof a.value === 'number' && a.value > 0 && a.price && a.price < 0.01);
  if (illiquidTokens.length > 0) riskAnalysis.push('Présence de tokens illiquides ou à très faible prix : ' + illiquidTokens.map(a => a.assetId).join(', '));
  return riskAnalysis;
}
